/*
 * Purpose: Execution log store — in-session + tenant-scoped localStorage persistence
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import { create } from 'zustand';
import { trackError } from '@/lib/siteAnalytics';
import { logsStorageKey } from '@/lib/tenantStorage';

export interface Log {
  id: string;
  timestamp: Date;
  level: 'info' | 'error' | 'success' | 'warning';
  message: string;
  workflowId?: string;
}

interface LogStore {
  logs: Log[];
  addLog: (level: Log['level'], message: string, workflowId?: string) => void;
  clearLogs: () => void;
  getLogs: (workflowId?: string) => Log[];
  reloadFromTenantStorage: () => void;
}

const MAX_LOGS = 100;

function parseStoredLogs(raw: string): Log[] {
  try {
    const parsed = JSON.parse(raw) as Log[];
    if (!Array.isArray(parsed)) return [];
    return parsed.map((log) => ({
      ...log,
      timestamp: new Date(log.timestamp),
    }));
  } catch {
    return [];
  }
}

function loadLogsFromStorage(): Log[] {
  if (typeof window === 'undefined') return [];
  const stored = localStorage.getItem(logsStorageKey());
  return stored ? parseStoredLogs(stored) : [];
}

function persistLogs(logs: Log[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(logsStorageKey(), JSON.stringify(logs.slice(0, MAX_LOGS)));
}

export const useLogStore = create<LogStore>((set, get) => ({
  logs: loadLogsFromStorage(),

  addLog: (level: Log['level'], message: string, workflowId?: string) => {
    const newLog: Log = {
      id: Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
      level,
      message,
      workflowId,
    };
    set((state) => {
      const logs = [newLog, ...state.logs].slice(0, MAX_LOGS);
      persistLogs(logs);
      return { logs };
    });
    if (level === 'error') {
      trackError(message, { workflowId });
    }
  },

  clearLogs: () => {
    persistLogs([]);
    set({ logs: [] });
  },

  getLogs: (workflowId?: string) => {
    const { logs } = get();
    if (!workflowId) return logs;
    return logs.filter((log) => log.workflowId === workflowId);
  },

  reloadFromTenantStorage: () => {
    set({ logs: loadLogsFromStorage() });
  },
}));
